import Card from "@/components/Card"
import FormHeader from "@/components/FormHeader"
import InputError from "@/components/InputError"
import Modal from "@/components/Modal"
import SkeletonRow from "@/components/SkeletonRow"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import api from "@/lib/axios"
import { DataSiswa, NilaiRapor as NilaiRaporType } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import React, { useState } from "react"
import toast from "react-hot-toast"
import { FaCheck, FaFaceSmileBeam, FaNoteSticky } from "react-icons/fa6"
import { GiBackwardTime } from "react-icons/gi"
import { IoWarning } from "react-icons/io5"
import { MdBlockFlipped } from "react-icons/md"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

const menus = [
  { number: 1, name: "Kelas 10 (Semester 1)", kelas: 10, semester: 1 },
  { number: 2, name: "Kelas 10 (Semester 2)", kelas: 10, semester: 2 },
  { number: 3, name: "Kelas 11 (Semester 1)", kelas: 11, semester: 1 },
  { number: 4, name: "Kelas 11 (Semester 2)", kelas: 11, semester: 2 },
  { number: 5, name: "Kelas 12 (Semester 1)", kelas: 12, semester: 1 },
]

type DataNilaiRapor = {
  id_data_pendaftaran: string
  nomor_pendaftaran: string
  siswa: DataSiswa
  nilai_rapor: Record<string, Record<string, NilaiRaporType[]>>
  status_loket_4_2: number
  keterangan_loket_4_2: string
}

type ValidasiState = {
  id_nilai: string
  validasi_ver1: number
  ket_ver1: string
}

type ValidasiGlobalState = {
  id_data_pendaftaran: string
  status_loket_4_2: number
  keterangan_loket_4_2: string
}

const patchDataValidasi = async (form: ValidasiState) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/loket-4/data-nilai-rapor/validasi",
    form
  )
  return response.data
}

const patchDataValidasiGlobal = async (form: ValidasiGlobalState) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/loket-4/data-nilai-rapor/validasi-global",
    form
  )
  return response.data
}

const patchDataResetValidasi = async (id_data_pendaftaran: string) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/reset?loket=4&menu=2",
    { id_data_pendaftaran }
  )
  return response.data
}

export default function NilaiRapor({
  data,
  isPending,
  error,
}: {
  data: DataNilaiRapor
  isPending: boolean
  error: AxiosError | null
}) {
  // const location = useLocation();
  // const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const idParams = searchParams.get("id")
  const submenuParams = searchParams.get("submenu")

  const [activeMenu, setActiveMenu] = useState(
    submenuParams ? Number(submenuParams) : 1
  )
  const [selectedNilaiRapor, setSelectedNilaiRapor] =
    useState<NilaiRaporType | null>(null)
  const [showCatatan, setShowCatatan] = useState(false)
  const [form, setForm] = useState({
    catatan: "",
  })

  const menu = menus.find((menu) => menu.number === activeMenu)
  const listNilaiRapor = menu
    ? data?.nilai_rapor?.[menu.kelas]?.[menu.semester] ?? []
    : []

  const queryClient = useQueryClient()
  const {
    mutate,
    isPending: isPendingValidation,
    error: errorValidation,
  } = useMutation({
    mutationFn: patchDataValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataNilaiRapor", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
      setShowCatatan(false)
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateValidationGlobal,
    isPending: isPendingValidationGlobal,
    error: errorValidationGlobal,
  } = useMutation({
    mutationFn: patchDataValidasiGlobal,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataNilaiRapor", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateResetValidation,
    isPending: isPendingResetValidation,
    // error: errorResetValidation,
  } = useMutation({
    mutationFn: patchDataResetValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataNilaiRapor", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleValidasi = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    mutate({
      id_nilai: selectedNilaiRapor?.id_nilai || "",
      validasi_ver1: selectedNilaiRapor?.validasi_ver1 || 0,
      ket_ver1: form.catatan,
    })
  }

  const handleValidasiGlobal = (status: number) => {
    mutateValidationGlobal({
      id_data_pendaftaran: data?.id_data_pendaftaran,
      status_loket_4_2: status,
      keterangan_loket_4_2: form.catatan,
    })
  }

  const handleReset = () => {
    mutateResetValidation(data?.id_data_pendaftaran || "")
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />
          })}
      </div>
    )
  }

  if (error?.response?.status === 404) {
    return (
      <div>
        Data <span className="text-primary">{idParams}</span> tidak ditemukan.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSmileBeam className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold">
            Lakukan pencarian menggunakan nomor pendaftaran atau nama siswa.
          </h1>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Alert variant={"warning"} className="z-50 mb-5 sticky top-0">
        <IoWarning />
        <AlertTitle>
          <strong>Perhatian:</strong>
        </AlertTitle>
        <AlertDescription>
          Semua nilai default status nilai rapor adalah Valid. Jika terdapat
          nilai yang tidak valid mohon di lakukan update status nilai menjadi
          tidak Valid spesifik per-nilai dan berikan catatan.
        </AlertDescription>
      </Alert>
      <div className="mt-5 px-3 flex justify-between border rounded-full">
        {menus.map((menu) => {
          return (
            <div
              key={menu.number}
              className={`p-2 ${
                activeMenu === menu.number
                  ? "text-primary"
                  : "cursor-pointer hover:text-muted-foreground"
              } `}
              onClick={() => {
                setActiveMenu(menu.number)
              }}
            >
              <h1 className="font-semibold">{menu.name}</h1>
            </div>
          )
        })}
      </div>
      <section className="mt-5 flex-1 overflow-auto">
        <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
          <thead>
            <tr>
              <th className="py-2 px-4 w-[40%] border font-semibold text-start">
                Mata Pelajaran
              </th>
              <th className="py-2 px-4 w-[10%] border font-semibold text-center">
                Nilai Pengetahuan
              </th>
              <th className="py-2 px-4 w-[20%] border font-semibold text-center">
                Status
              </th>
              <th className="py-2 px-4 w-[30%] border font-semibold text-start">
                Catatan Tidak Valid
              </th>
            </tr>
          </thead>
          <tbody>
            {listNilaiRapor.length > 0
              ? listNilaiRapor.map((item) => {
                  const currentItem =
                    selectedNilaiRapor &&
                    selectedNilaiRapor?.id_nilai === item.id_nilai
                      ? selectedNilaiRapor
                      : item

                  return (
                    <tr key={item.id_nilai}>
                      <td className="py-2 px-4 border">
                        {item.nama_mata_pelajaran}
                      </td>
                      <td className="py-2 px-4 border text-center">
                        {item.nilai_skala_100}
                      </td>
                      <td className="py-2 px-4 border">
                        <div>
                          <Select
                            value={
                              currentItem.validasi_ver1 === 2
                                ? "Tidak Valid"
                                : "Valid"
                            }
                            onValueChange={(value) => {
                              const status = value === "Tidak Valid" ? 2 : 1

                              setSelectedNilaiRapor({
                                ...item,
                                validasi_ver1: status,
                              })

                              if (status === 2) setShowCatatan(true)
                              else
                                mutate({
                                  id_nilai: item.id_nilai || "",
                                  validasi_ver1: 1,
                                  ket_ver1: "",
                                })
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={"Pilih status"} />
                            </SelectTrigger>
                            <SelectContent side="top">
                              {["Valid", "Tidak Valid"].map((pageSize) => (
                                <SelectItem
                                  key={pageSize}
                                  value={`${pageSize}`}
                                >
                                  {pageSize}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </td>
                      <td className="py-2 px-4 border">
                        {isPendingValidation &&
                        selectedNilaiRapor?.id_nilai === item.id_nilai ? (
                          <div className="flex items-center gap-3">
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            Processing...
                          </div>
                        ) : (
                          item.ket_ver1
                        )}
                      </td>
                    </tr>
                  )
                })
              : "Nilai rapor tidak ditemukan"}
          </tbody>
        </table>

        <Card className="mt-10 flex-1 overflow-auto">
          <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
            <tbody>
              <tr>
                <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                  Status Validasi
                </th>
                <td className="py-2 px-4 border w-[5%]">:</td>
                <td className="py-2 px-4 border w-[50%]">
                  {data?.status_loket_4_2 === 1 ? (
                    <Badge variant={"dark"}>Sudah Valid</Badge>
                  ) : data?.status_loket_4_2 === 0 ? (
                    <Badge variant={"outline"}>Tidak Valid</Badge>
                  ) : (
                    <Badge variant={"secondary"}>Belum Valid</Badge>
                  )}
                </td>
              </tr>
              <tr>
                <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                  Catatan
                </th>
                <td className="py-2 px-4 border w-[5%]">:</td>
                <td className="py-2 px-4 border w-[50%]">
                  {data?.keterangan_loket_4_2}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mt-5 px-4">
            {data?.status_loket_4_2 === null ? (
              <>
                <h1 className="font-semibold text-xl">
                  Kesimpulan Hasil Validasi
                </h1>
                <p className="text-muted-foreground">
                  Jika terlalu banyak nilai yang tidak sesuai maka kesimpulan
                  status validasi dapat dinyatakan tidak valid.
                </p>
                <div className="mt-3">
                  <Label>Input Catatan</Label>
                  <Textarea
                    placeholder="Ketik catatan..."
                    className="mt-1"
                    value={form.catatan}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        catatan: event.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={errorValidationGlobal?.response?.data.errors?.keterangan_loket_4_2?.join(
                      ","
                    )}
                  />
                </div>
                <div className="mt-5 flex justify-end gap-3">
                  {isPendingValidationGlobal ? (
                    <Button
                      variant={"outline"}
                      className="flex items-center gap-3"
                      disabled
                    >
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Processing...
                      </>
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant={"outline"}
                        className="flex items-center gap-3"
                        onClick={() => handleValidasiGlobal(0)}
                        disabled={isPendingValidationGlobal}
                      >
                        <MdBlockFlipped />
                        Tidak Valid
                      </Button>
                      <Button
                        className="flex items-center gap-3"
                        onClick={() => handleValidasiGlobal(1)}
                        disabled={isPendingValidationGlobal}
                      >
                        <FaCheck />
                        Valid
                      </Button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  className="flex items-center gap-3"
                  onClick={() => handleReset()}
                  disabled={isPendingResetValidation}
                >
                  {isPendingResetValidation ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <GiBackwardTime />
                      Reset Hasil Validasi
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </section>

      <Modal show={showCatatan} onClose={() => setShowCatatan(false)}>
        <form onSubmit={handleValidasi}>
          <FormHeader
            title={`${selectedNilaiRapor?.nama_mata_pelajaran} dengan nilai ${selectedNilaiRapor?.nilai_skala_100} tidak valid`}
            description="Masukan alasan kenapa tidak valid (contoh: nilai yang tercantum dirapor adalah 99"
            icon={<FaNoteSticky />}
          />
          <div>
            <Label>Input Catatan</Label>
            <Textarea
              placeholder="Ketik catatan..."
              className="mt-1"
              value={form.catatan}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, catatan: event.target.value }))
              }
            />
            <InputError
              text={errorValidation?.response?.data.errors?.ket_ver1?.join(",")}
            />
          </div>
          <div className="mt-5 flex justify-end gap-3">
            <Button
              type="button"
              variant={"outline"}
              onClick={() => {
                setShowCatatan(false)
                setSelectedNilaiRapor(null)
                setForm((prev) => ({ ...prev, catatan: "" }))
              }}
              disabled={isPendingValidation}
            >
              Batal
            </Button>
            <Button disabled={isPendingValidation}>
              {isPendingValidation ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Simpan"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
